import React from 'react';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  Ban,
  Wrench,
  Smartphone,
  DollarSign,
  Layers,
  HelpCircle,
  TrendingUp,
  ClipboardList,
  Sparkles,
  CheckCircle2,
  Hammer,
  Users,
  Building,
  Zap,
  FileText,
  AlertTriangle
} from 'lucide-react';
import Logo from '../Logo';
import { SEO } from '../SEO';
import { Button } from '../ui/Button';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'wouter';
import { SupportFooter } from '../SupportFooter';

const painPoints = [
  {
    icon: DollarSign,
    title: "Paying for Software You Barely Use",
    description: "Monthly fees add up fast when you're not even using half the features."
  },
  {
    icon: Smartphone,
    title: "Tools That Don't Work on Mobile",
    description: "You're on job sites all day — software that needs a desktop is useless."
  },
  {
    icon: Layers,
    title: "Too Many Apps Doing the Same Thing",
    description: "Invoicing here, scheduling there, payments somewhere else. It's a mess."
  },
  {
    icon: HelpCircle,
    title: "Advice Built for Tech Companies",
    description: "\"Best software\" lists that recommend enterprise tools you'll never need."
  },
  {
    icon: TrendingUp,
    title: "Not Knowing What to Use as You Grow",
    description: "What works for just you won't work when you have a crew."
  }
];

const blueprintComponents = [
  {
    icon: Wrench,
    name: "Core Stack",
    description: "What to use right now",
    detail: "The essentials for running your trade business today"
  },
  {
    icon: TrendingUp,
    name: "Growth Stack",
    description: "What to add as your business grows",
    detail: "Tools to bring on when you hire or take on more jobs"
  },
  {
    icon: Ban,
    name: "Avoid List",
    description: "Tools to skip so you don't overpay",
    detail: "Enterprise software and overbuilt tools that waste your money"
  },
  {
    icon: FileText,
    name: "Plain-English Rationale",
    description: "Why each tool fits your situation",
    detail: "No jargon — just straight talk about what works and why"
  },
  {
    icon: Zap,
    name: "Upgrade Path",
    description: "When to change or add tools",
    detail: "Clear triggers so you know exactly when to level up"
  }
];

const steps = [
  {
    icon: ClipboardList,
    title: "Answer a Few Questions",
    description: "Tell us about your trade, team size, and how you work"
  },
  {
    icon: Sparkles,
    title: "We Analyze What Fits",
    description: "Your answers matched against tools that actually work for trades"
  },
  {
    icon: CheckCircle2,
    title: "Get Your Blueprint",
    description: "A clear software plan you can act on immediately"
  }
];

const whoThisIsFor = [
  "Solo tradespeople running everything yourself",
  "Small crews and service teams (1-5 people)",
  "New trade businesses setting things up right from the start",
  "Established trades tired of messy, disconnected systems",
  "Anyone who wants clarity instead of endless research"
];

const differentiators = [
  {
    icon: Hammer,
    text: "Built specifically for service businesses"
  },
  {
    icon: Ban,
    text: "No enterprise overkill — tools sized for trades"
  },
  {
    icon: Shield,
    text: "No paid placements in recommendations"
  },
  {
    icon: AlertTriangle,
    text: "Tells you what NOT to use"
  },
  {
    icon: CreditCard,
    text: "One-time payment — no subscriptions"
  }
];

const faqItems = [
  {
    question: "What trades is this for?",
    answer: "Any field service or trade business: plumbers, electricians, HVAC, cleaners, landscapers, handymen, pressure washers, painters, contractors, and more."
  },
  {
    question: "How long does it take?",
    answer: "About 3 minutes to answer the questions, then you get your Blueprint instantly."
  },
  {
    question: "What if it doesn't help?",
    answer: "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you — no questions asked."
  }
];

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Choose Software for Your Trade or Service Business",
  "description": "Get a no-nonsense software blueprint built specifically for trades, contractors, and field service businesses",
  "totalTime": "PT3M",
  "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "29" },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Tell Us About Your Trade Business",
      "text": "Answer a few questions about your trade type (plumber, electrician, HVAC, cleaner, landscaper, etc.), team size, and how you currently run jobs.",
      "url": "https://btoolme.com/questionnaire",
      "itemListElement": [
        { "@type": "HowToTip", "text": "Trade businesses need mobile-first tools — make sure to mention if you're running jobs in the field." },
        { "@type": "HowToTip", "text": "Solo operators need different tools than small crews — your team size matters for our recommendations." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "We Find Tools Built for Trades",
      "text": "Your profile is matched against field service, invoicing, scheduling, and job management tools that actually work for trade businesses — not enterprise software.",
      "itemListElement": [
        { "@type": "HowToTip", "text": "We filter out tools built for tech companies and recommend software designed for service businesses." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Get Your Trade Business Software Blueprint",
      "text": "Receive a clear, actionable plan: what to use right now, what to add as you hire, what to avoid, and plain-English reasons for every recommendation.",
      "url": "https://btoolme.com/unlock",
      "itemListElement": [
        { "@type": "HowToTip", "text": "Your Blueprint includes an Avoid List of overbuilt tools that waste money for trade businesses." }
      ]
    }
  ]
};

export function TradesLandingPage() {
  const [_, navigate] = useLocation();

  const seoTitle = "Software for Trades, Contractors & Service Businesses | btoolme";
  const seoDescription = "Get a personalized software blueprint for trades and service businesses. Find the right tools for job sites, invoicing, and getting paid — without wasting money on the wrong software.";

  const guaranteeText = "If you don't feel 100% confident in your software decisions after using btoolme, we'll refund you — no questions asked.";

  return (
    <div className="min-h-screen flex flex-col" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/for-trades"
        faqItems={faqItems}
        keywords="software for trades, contractor software, plumber software, electrician software, HVAC software, field service software, trade business software, service business tools, invoicing for contractors, scheduling software trades, job management software, tradesman apps, contractor management software, field service management"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-slate-200/40 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-amber-100/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-100/50 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <Logo className="mb-8 mx-auto" />

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              Stop Guessing Which Software to Use in Your Trade Business
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-4 font-medium max-w-3xl mx-auto">
              Get a clear, no-nonsense software blueprint built for job sites, field work, invoicing, and getting paid — without wasting money on the wrong tools.
            </p>

            <p className="text-sm text-gray-500 mb-8">
              Built specifically for service businesses — not generic "best software" lists.
            </p>

            <p className="text-sm text-gray-600 font-medium mb-10">
              Answer a few questions. Get a clear plan. Move on.
            </p>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-8 py-4 text-lg sparkle-button w-full sm:w-auto"
                size="lg"
                aria-label="Build your personalized software blueprint"
              >
                Build My Software Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-sm text-gray-500 max-w-md text-center">
                {guaranteeText}
              </p>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-600" />
                <span>One-time $29</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-slate-600" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm text-gray-500 mb-6">
              Perfect for plumbers, electricians, HVAC techs, contractors, cleaners, landscapers, and other service trades.
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Why Software Is Hard for Trade Businesses
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Running a trade business is hard enough. Your software shouldn't make it harder.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {painPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className={`flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100 ${
                    index === painPoints.length - 1 ? 'sm:col-span-2 sm:max-w-md sm:mx-auto' : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blueprint Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Software for Service Businesses, Simplified
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
              This isn't a list of tools. It's a clear decision — your Business Software Blueprint.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {blueprintComponents.slice(0, 3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{component.description}</p>
                  <p className="text-slate-600 text-sm font-medium">{component.detail}</p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5 max-w-2xl mx-auto lg:max-w-none lg:px-24">
            {blueprintComponents.slice(3).map((component, index) => {
              const Icon = component.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-slate-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">{component.name}</h3>
                  <p className="text-gray-600 text-sm mb-2">{component.description}</p>
                  <p className="text-slate-600 text-sm font-medium">{component.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-slate-300 to-transparent" />
                  )}

                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-slate-600 to-slate-700 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/20">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-500 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Who This Is For
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sm:p-8">
            <ul className="space-y-4">
              {whoThisIsFor.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What Makes This Different */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              A Better Way to Choose Software for Service Businesses
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.slice(0, 3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100"
                >
                  <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-2xl mx-auto">
            {differentiators.slice(3).map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100"
                >
                  <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-slate-700" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-slate-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Know you're using the right tools — and move on.
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get your Business Software Blueprint and stop second-guessing your setup.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={() => navigate('/questionnaire')}
              className="group px-10 py-5 text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Software Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-sm text-gray-400 max-w-md text-center mt-2">
              {guaranteeText}
            </p>
          </div>

          <p className="mt-10 text-sm text-gray-500">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-400 hover:text-white underline">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-10 bg-slate-900 border-t border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4 text-center">Related Guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href="/tools" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Browse All 100+ Tools</p>
              <p className="text-xs text-slate-400">Full curated business software directory</p>
            </a>
            <a href="/tools-needed-to-start-a-business" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Starting a Business?</p>
              <p className="text-xs text-slate-400">Essential tools to launch without overbuying</p>
            </a>
            <a href="/what-software-should-a-small-business-use" className="group p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400">
              <p className="font-medium text-white text-sm mb-1">Small Business Software Guide</p>
              <p className="text-xs text-slate-400">How to choose the right software for any business</p>
            </a>
          </div>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-slate-900" />
    </div>
  );
}
